import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControlLabel,
  Switch,
  Alert,
  CircularProgress,
  Typography,
  GlobalStyles,
  Paper,
} from '@mui/material';
import {
  CheckCircle,
  PointOfSale,
  LocationOn,
  Settings,
} from '@mui/icons-material';

const initialFormState = {
  name: '',
  terminal_id: '',
  location: '',
  notes: '',
  is_active: true,
};

// Aggressive CSS overrides to defeat Bulma - KEEPS BORDERS INTACT
const bulmaOverrides = {
  '.terminal-form-modal-wrapper': {
    // Reset ALL Bulma input styling
    '& input, & select, & textarea': {
      boxShadow: 'none !important',
      border: 'none !important',
      padding: '0 !important',
      height: 'auto !important',
      lineHeight: 'inherit !important',
      backgroundColor: 'transparent !important',
      color: 'inherit !important',
      fontSize: 'inherit !important',
      fontFamily: 'inherit !important',
      width: 'auto !important',
      maxWidth: 'none !important',
      margin: '0 !important',
      borderRadius: '0 !important',
      '&:focus, &:active, &:hover': {
        border: 'none !important',
        boxShadow: 'none !important',
        outline: 'none !important',
      },
    },
    // CRITICAL: Force MUI TextField borders to show - DO NOT REMOVE
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#fff !important',
      borderRadius: '8px !important',
      transition: 'all 0.2s ease-in-out !important',
      overflow: 'visible !important',
      '& fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.23) !important',
        borderWidth: '1px !important',
        borderStyle: 'solid !important',
        borderRadius: '8px !important',
      },
      '&:hover fieldset': {
        borderColor: 'rgba(0, 0, 0, 0.5) !important',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#2563eb !important',
        borderWidth: '2px !important',
      },
      '&.Mui-error fieldset': {
        borderColor: '#dc2626 !important',
      },
      '& input': {
        textOverflow: 'clip !important',
      },
      '& textarea': {
        textOverflow: 'clip !important',
      },
    },
    '& .MuiInputBase-input': {
      padding: '14px 16px !important',
      fontSize: '15px !important',
      lineHeight: '1.5 !important',
      color: '#1f2937 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      fontWeight: '400 !important',
    },
    '& .MuiInputLabel-root': {
      fontSize: '15px !important',
      color: '#6b7280 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      fontWeight: '500 !important',
      '&.Mui-focused': {
        color: '#2563eb !important',
        fontWeight: '600 !important',
      },
      '&.Mui-error': {
        color: '#dc2626 !important',
      },
    },
    '& .MuiFormHelperText-root': {
      fontSize: '13px !important',
      margin: '6px 14px 0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      lineHeight: '1.4 !important',
    },
    // Dialog styling
    '& .MuiDialog-paper': {
      maxWidth: '600px !important',
      width: '100% !important',
      borderRadius: '12px !important',
    },
    '& .MuiDialogTitle-root': {
      padding: '28px 32px 20px !important',
      fontSize: '24px !important',
      fontWeight: '700 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#111827 !important',
      background: 'linear-gradient(to bottom, #f9fafb, #ffffff) !important',
      borderBottom: '1px solid #e5e7eb !important',
    },
    '& .MuiDialogContent-root': {
      padding: '24px 32px !important',
      backgroundColor: '#f9fafb !important',
    },
    '& .MuiDialogActions-root': {
      padding: '20px 32px 24px !important',
      backgroundColor: '#ffffff !important',
      borderTop: '1px solid #e5e7eb !important',
      gap: '12px !important',
    },
    // Typography
    '& .MuiTypography-h6': {
      fontSize: '18px !important',
      fontWeight: '700 !important',
      margin: '0 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      color: '#111827 !important',
    },
    // Buttons
    '& .MuiButton-root': {
      textTransform: 'none !important',
      fontSize: '15px !important',
      fontWeight: '600 !important',
      padding: '10px 24px !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
      borderRadius: '8px !important',
      minHeight: '40px !important',
      '&.MuiButton-contained': {
        boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06) !important',
        '&:hover': {
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06) !important',
        },
      },
    },
    // Switch styling
    '& .MuiSwitch-root': {
      '& .MuiSwitch-switchBase.Mui-checked': {
        color: '#2563eb !important',
        '& + .MuiSwitch-track': {
          backgroundColor: '#2563eb !important',
        },
      },
    },
    '& .MuiFormControlLabel-label': {
      fontSize: '15px !important',
      fontWeight: '500 !important',
      color: '#374151 !important',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif !important',
    },
  },
};

export function TerminalFormModal({
  open,
  onClose,
  onSave,
  mode = 'create',
  terminal,
}) {
  const [formData, setFormData] = useState(initialFormState);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && terminal) {
        setFormData({
          name: terminal.name || '',
          terminal_id: terminal.terminal_id || '',
          location: terminal.location || '',
          notes: terminal.notes || '',
          is_active: terminal.is_active ?? true,
        });
      } else {
        setFormData(initialFormState);
      }
      setError(null);
    }
  }, [open, mode, terminal]);

  const handleChange = (field) => (event) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async () => {
    // Basic validation
    if (!formData.name.trim()) {
      setError('Terminal name is required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save terminal');
    } finally {
      setSaving(false);
    }
  };

  const isCreate = mode === 'create';
  const title = isCreate ? 'Add Terminal' : 'Edit Terminal';

  return (
    <>
      <GlobalStyles styles={bulmaOverrides} />
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        className="terminal-form-modal-wrapper"
      >
        <DialogTitle>{title}</DialogTitle>

        <DialogContent dividers>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError(null)}
              sx={{
                mb: 3,
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              {error}
            </Alert>
          )}

          {/* Terminal Information */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <PointOfSale sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Terminal Information</Typography>
            </div>
            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Terminal Name"
                  value={formData.name}
                  onChange={handleChange('name')}
                  required
                  placeholder="e.g., Main Register, Online Store"
                  helperText="A friendly name to identify this terminal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Terminal ID"
                  value={formData.terminal_id}
                  onChange={handleChange('terminal_id')}
                  placeholder={isCreate ? 'Leave blank to auto-generate' : 'Leave blank to keep current ID'}
                  helperText={isCreate
                    ? 'Unique ID — left blank it is generated from the name (e.g., front_counter_01)'
                    : 'Unique ID — leave blank to keep the current ID'}
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Location & Notes */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              mb: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <LocationOn sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Location & Notes</Typography>
            </div>
            <Grid container rowSpacing={3} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Location"
                  value={formData.location}
                  onChange={handleChange('location')}
                  placeholder="e.g., Front Counter, 123 Main St"
                  helperText="Physical location of the terminal"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Notes"
                  value={formData.notes}
                  onChange={handleChange('notes')}
                  multiline
                  rows={3}
                  placeholder="Internal notes about this terminal"
                  helperText="Any additional information (not shown to customers)"
                />
              </Grid>
            </Grid>
          </Paper>

          {/* Status Settings */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              backgroundColor: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '12px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '10px',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ffffff',
              }}>
                <Settings sx={{ fontSize: '22px' }} />
              </div>
              <Typography variant="h6">Status Settings</Typography>
            </div>
            <Grid container rowSpacing={2} columnSpacing={{ xs: 2, sm: 2.5, md: 3 }}>
              <Grid item xs={12}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    backgroundColor: formData.is_active ? '#f0fdf4' : '#fef2f2',
                    border: `1px solid ${formData.is_active ? '#bbf7d0' : '#fecaca'}`,
                    borderRadius: '8px',
                  }}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.is_active}
                        onChange={handleChange('is_active')}
                      />
                    }
                    label={
                      <div>
                        <Typography sx={{ fontWeight: 600, fontSize: '15px', color: '#111827' }}>
                          Active
                        </Typography>
                        <Typography sx={{ fontSize: '13px', color: '#6b7280' }}>
                          {formData.is_active ? 'Terminal can accept payments' : 'Terminal is disabled'}
                        </Typography>
                      </div>
                    }
                    sx={{ m: 0, width: '100%' }}
                  />
                </Paper>
              </Grid>
            </Grid>
          </Paper>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={onClose}
            disabled={saving}
            sx={{
              color: '#6b7280',
              '&:hover': {
                backgroundColor: '#f3f4f6',
                color: '#111827',
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={18} sx={{ color: '#fff' }} /> : <CheckCircle />}
            sx={{
              background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
              '&:hover': {
                background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
              },
              '&:disabled': {
                background: '#e5e7eb',
                color: '#9ca3af',
              }
            }}
          >
            {saving ? 'Saving...' : isCreate ? 'Add Terminal' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
